import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { useSession } from '@/hooks/use-session';
import { GOOGLE_SIGN_IN_URL, rememberReturnTo, type ReturnTo } from '@/lib/oauth';
import { onIdentityChanged } from '@/lib/session-change';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { nativeGoogleSignInMessage, requestGoogleIdToken } from '@/native/google-auth';
import { isNativePlatform } from '@/native/platform';

/**
 * "Continue with Google" — or nothing at all.
 *
 * Google sign-in is optional on this server (`config.google`), and `auth.me` reports whether it
 * is configured. Drawing the button anyway would mean a press that ends on a 404, so the whole
 * block — divider included — is absent when the provider is off, and while `me` is still in
 * flight.
 *
 * TWO WAYS IN, AND THEY ARE NOT THE SAME CONTROL.
 *
 * On the web it is an anchor rather than a `<Button onClick>`: `/auth/google` answers with a
 * cross-origin 302, which a fetch cannot follow to a page. This has to be an ordinary
 * navigation, and it is exactly what it always was.
 *
 * In the native shell that navigation is impossible — Google refuses OAuth inside an embedded
 * WebView (`disallowed_useragent`) — so the shell opens the OS account picker and posts the
 * `id_token` it returns to `auth.googleNative`. That is a button, because it is a call.
 */
export function GoogleSignInButton({ returnTo }: { returnTo: ReturnTo }) {
  const { authProviders } = useSession();

  if (!authProviders.google) return null;

  // Native and no client id would be a button that cannot work, which is worse than no button —
  // the same call the block above makes. The two travel together on `me`, so this is a guard
  // rather than a state anybody should see.
  const native = isNativePlatform();
  if (native && authProviders.googleClientId === null) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          or
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      {native && authProviders.googleClientId !== null ? (
        <NativeGoogleButton webClientId={authProviders.googleClientId} returnTo={returnTo} />
      ) : (
        <a
          href={GOOGLE_SIGN_IN_URL}
          // Recorded on the way out, read back by `useGoogleAuthLanding` on the way in: the
          // server always returns the browser to WEB_APP_URL and knows nothing of this intent.
          onClick={() => rememberReturnTo(returnTo)}
          className={cn(buttonVariants({ variant: 'outline' }), 'mt-4 w-full')}
        >
          Continue with Google
        </a>
      )}
    </div>
  );
}

/**
 * The shell's half: picker, exchange, land.
 *
 * `rememberReturnTo` has no part here. It exists because the redirect dance leaves the app
 * entirely and comes back with nothing but a query string; this never leaves the router, so the
 * destination is simply navigated to.
 *
 * Nothing is retried and nothing is queued. A press that failed leaves its sentence on screen
 * and the button ready — a second press opens a second picker, which is what a player who just
 * tapped Cancel by accident expects.
 */
function NativeGoogleButton({
  webClientId,
  returnTo,
}: {
  webClientId: string;
  returnTo: ReturnTo;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signIn = trpc.auth.googleNative.useMutation();
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const press = async () => {
    setMessage(null);
    setPending(true);
    try {
      const idToken = await requestGoogleIdToken(webClientId);
      await signIn.mutateAsync({ idToken });
      await onIdentityChanged(queryClient);
      await navigate({ to: returnTo });
    } catch (error) {
      // `null` is a dismissed picker: the player said no, and telling them so is noise.
      setMessage(nativeGoogleSignInMessage(error));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="mt-4 w-full"
        disabled={pending}
        onClick={() => void press()}
      >
        {pending ? 'Signing in…' : 'Continue with Google'}
      </Button>

      {message !== null && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {message}
        </p>
      )}
    </>
  );
}
